/*
 * Guarantees a product form submits exactly one, correct Submarine presale
 * `selling_plan` for the selected variant. Without the plan on the line item,
 * Submarine does not count the order against the presale limit and the product
 * oversells.
 *
 * The Submarine app block already renders a selling_plan input bound (via the
 * `form` attribute) to the main product form, but it does not cover the sticky
 * add-to-cart form and is not re-synced when the variant changes. This element
 * reconciles every selling_plan input associated with its form down to a single
 * active one and keeps its value in step with the selected variant, using the
 * variant-id -> plan-id map rendered by snippets/presale-selling-plan.liquid.
 *
 * It also exposes the server-resolved Submarine availability map to the variant picker.
 * Shopify's native `variant.available` remains true after a Submarine limit is reached.
 *
 * Its own fallback input ships disabled so that, before this script runs, it can
 * never become a second submitted value alongside the app block's input.
 */
if (!customElements.get('presale-selling-plan')) {
  class PresaleSellingPlan extends HTMLElement {
    connectedCallback() {
      this.fallbackInput = this.querySelector('input[name="selling_plan"]');
      this.form = this.closest('form');
      this.section = this.dataset.section;

      this.planMap = this.readMap('script[data-presale-plan-map]');
      this.availabilityMap = this.readMap('script[data-presale-availability-map]');

      this.sync = this.sync.bind(this);

      const switchers = document.querySelectorAll(
        `variant-radios[data-section="${this.section}"], variant-selects[data-section="${this.section}"], variant-selects-custom[data-section="${this.section}"]`
      );
      switchers.forEach((switcher) => {
        // Run after Dawn has written the new variant id into the form.
        switcher.addEventListener('change', () => setTimeout(this.sync, 0));
      });

      // Dawn dispatches a bubbling `change` on the id input when it updates the variant.
      if (this.form) {
        this.form.addEventListener('change', (event) => {
          if (event.target && event.target.name === 'id') this.sync();
        });
      }

      this.sync();
    }

    readMap(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '{}');
      } catch {
        return {};
      }
    }

    isVariantAvailable(variantId) {
      return this.availabilityMap[variantId] !== false;
    }

    getVariantId() {
      // Several controls can be named "id" (a disabled fallback input plus the
      // form-associated variant <select>/radios). The submitted id is the enabled
      // one with a value, so mirror that here.
      const control = Array.from(this.form.elements).find(
        (el) => el.name === 'id' && !el.disabled && el.value
      );
      return control ? control.value : '';
    }

    sync() {
      if (!this.form) return;

      // Every selling_plan input associated with this form, including the app
      // block's input bound via the `form` attribute.
      const inputs = Array.from(this.form.elements).filter(
        (el) => el.name === 'selling_plan'
      );
      if (!inputs.length) return;

      // Prefer the app block's input when present; otherwise use our fallback.
      const target = inputs.find((el) => el !== this.fallbackInput) || this.fallbackInput;
      inputs.forEach((el) => {
        if (el !== target) el.disabled = true;
      });

      const variantId = this.getVariantId();
      const planId = variantId ? this.planMap[variantId] : undefined;

      if (planId) {
        target.value = planId;
        target.disabled = false;
      } else {
        target.value = '';
        target.disabled = true;
      }
    }
  }

  customElements.define('presale-selling-plan', PresaleSellingPlan);
}
